import { useEffect, useRef, useState } from 'react';
import { Seo } from '../components/Seo';
import { buildTitle, SITE } from '../lib/site';

const API_BASE = `${SITE.url}/api`;

export function DocsPage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const scriptId = 'swagger-ui-bundle';
    const styleId = 'swagger-ui-css';

    const loadSwagger = () => {
      if (cancelled || !containerRef.current) return;
      const w = window as unknown as { SwaggerUIBundle?: any };
      const SwaggerUI = w.SwaggerUIBundle;
      if (!SwaggerUI) {
        setError('Failed to load API documentation UI. Please refresh.');
        return;
      }
      SwaggerUI({
        url: `${API_BASE}/openapi.json`,
        dom_id: '#swagger-ui',
        deepLinking: true,
        docExpansion: 'none',
        defaultModelsExpandDepth: 1,
        defaultModelExpandDepth: 1,
        filter: true,
        presets: [SwaggerUI.presets.apis],
        plugins: [SwaggerUI.plugins.DownloadUrl],
      });
      setLoaded(true);
    };

    const injectStyles = () => {
      if (document.getElementById(styleId)) return;
      const link = document.createElement('link');
      link.id = styleId;
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/swagger-ui-dist@5/swagger-ui.css';
      document.head.appendChild(link);
    };

    const injectScript = () => {
      const existing = document.getElementById(scriptId);
      if (existing) {
        if ((existing as HTMLScriptElement).dataset.loaded === 'true') loadSwagger();
        else existing.addEventListener('load', loadSwagger);
        return;
      }
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://cdn.jsdelivr.net/npm/swagger-ui-dist@5/swagger-ui-bundle.js';
      script.async = true;
      script.onload = () => {
        script.dataset.loaded = 'true';
        loadSwagger();
      };
      script.onerror = () => setError('Failed to load Swagger UI from CDN. Check your network connection.');
      document.body.appendChild(script);
    };

    injectStyles();
    injectScript();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Seo
        title={buildTitle('API Documentation')}
        description="ShieldMyAss API documentation. Public REST endpoints for live rates, swap creation and order status. Provider contract for liquidity partners. OpenAPI 3.0 specification available."
        path="/docs"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'TechArticle',
          headline: 'ShieldMyAss API Documentation',
          description:
            'Public and provider-facing REST API documentation for the ShieldMyAss non-custodial crypto swap service.',
          url: `${SITE.url}/docs`,
          author: { '@type': 'Organization', name: SITE.name, url: SITE.url },
          publisher: { '@type': 'Organization', name: SITE.name, url: SITE.url },
          about: {
            '@type': 'APIReference',
            name: 'ShieldMyAss API',
            targetProduct: { '@type': 'SoftwareApplication', name: SITE.name, applicationCategory: 'FinanceApplication' },
            executableExample: `${API_BASE}/openapi.json`,
          },
        }}
      />

      <section className="page-header">
        <div className="container">
          <span className="eyebrow">Developers</span>
          <h1>API Documentation</h1>
          <p>
            Public REST endpoints for live rates, swap creation and order status. OpenAPI 3.0 spec at{' '}
            <a href={`${API_BASE}/openapi.json`}>{API_BASE.replace('https://www.', '')}/openapi.json</a>.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="prose">
            <div className="callout">
              <div className="title">Two API surfaces</div>
              <p>
                <strong>Public</strong> endpoints under <code>/api/*</code> power the exchange widget and
                require no authentication.
                <br />
                <strong>Provider</strong> endpoints under <code>/api/provider/*</code> expose the
                liquidity-provider contract (currencies, pairs, quotes, orders, status) and require a
                partner API key passed in the <code>X-Provider-Key</code> header.
              </p>
            </div>

            <h2>Base URL</h2>
            <pre><code>{API_BASE}</code></pre>

            <h2>Authentication</h2>
            <p>
              Provider endpoints require an API key issued by ShieldMyAss. Pass it in the{' '}
              <code>X-Provider-Key</code> header. To request a key, contact{' '}
              <a href={`mailto:${SITE.email}`}>{SITE.email}</a>.
            </p>

            <h2>Rate limits</h2>
            <ul>
              <li>Public endpoints: 30 requests/minute per IP</li>
              <li>Provider endpoints: 60 requests/minute per key</li>
              <li>Quote and order endpoints: 10 requests/second burst</li>
            </ul>

            <h2>Full interactive documentation</h2>
          </div>

          {error && (
            <div className="callout" style={{ margin: '1rem 0', borderColor: '#c43' }}>
              <p>{error}</p>
            </div>
          )}

          {!loaded && !error && (
            <div style={{ padding: '2rem', textAlign: 'center', opacity: 0.7 }}>
              Loading interactive API reference…
            </div>
          )}

          <div
            id="swagger-ui"
            ref={containerRef}
            style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '1rem',
              marginTop: '1rem',
              minHeight: '400px',
            }}
          />

          <div className="prose" style={{ marginTop: '2rem' }}>
            <h2>Quickstart</h2>
            <p>Get a live rate for BTC → USDT (TRON):</p>
            <pre><code>{`curl -X POST ${API_BASE}/rate \\
  -H "Content-Type: application/json" \\
  -d '{
    "fromCurrency": "btc",
    "toCurrency": "usdt",
    "toNetwork": "trx",
    "fromAmount": "0.5",
    "flow": "standard"
  }'`}</code></pre>

            <p>Fetch the supported currency list:</p>
            <pre><code>{`curl ${API_BASE}/currencies`}</code></pre>

            <p>Create a swap order:</p>
            <pre><code>{`curl -X POST ${API_BASE}/create \\
  -H "Content-Type: application/json" \\
  -d '{
    "fromCurrency": "btc",
    "toCurrency": "eth",
    "fromAmount": "0.5",
    "payoutAddress": "0xYOUR_ETH_ADDRESS",
    "flow": "standard"
  }'`}</code></pre>

            <h2>Support</h2>
            <p>
              Integration questions: <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
